'use client'

import { useQuery } from '@tanstack/react-query'
import useCartProduct from '@/app/[locale]/_hooks/useCartProduct'
import { IRedux } from '@/app/[locale]/_store/types'
import { IProduct, ISavedModifier } from '@/app/[locale]/_types'
import {
  fetchDirectCartModifiers,
  getOrderingDeliveryType,
  getSelectedMenuId,
} from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

function getCartLookupIds(product: IProduct): string[] {
  if (product.type === 'origin') {
    const variantIds =
      product.variant_products
        ?.map((variant: { id?: string }) => variant?.id)
        .filter(Boolean) ?? []

    if (variantIds.length > 0) {
      return variantIds as string[]
    }
  }

  return [product.id]
}

export default function useProductCardCart(product: IProduct) {
  const { branch } = useSelector((state: IRedux) => state.common)
  const { is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const selectedMenuId = getSelectedMenuId({
    isHall: is_hall === 'true',
    pathnameEndsWithMenu: false,
    menuId: menu_id,
    branchMenuId: branch?.menu_id,
  })

  const deliveryType = getOrderingDeliveryType({
    isHall: is_hall === 'true',
    orderType: order_type?.type,
  })

  const lookupIds = useMemo(() => getCartLookupIds(product), [product])
  const primaryId = lookupIds[0] || product.id

  const needsDirectModifiers =
    Boolean(product.has_modifier) &&
    product.type === 'simple' &&
    Boolean(selectedMenuId)

  const { data: directCartModifiers, isFetched } = useQuery({
    queryKey: [
      'product-card-direct-modifiers',
      product.id,
      selectedMenuId,
      deliveryType,
    ],
    queryFn: () =>
      fetchDirectCartModifiers(product.id, selectedMenuId, deliveryType),
    enabled: needsDirectModifiers,
    staleTime: 60_000,
  })

  const cartModifiers: ISavedModifier[] | null | undefined = !product.has_modifier
    ? undefined
    : product.type === 'simple'
      ? needsDirectModifiers
        ? isFetched
          ? (directCartModifiers ?? null)
          : null
        : null
      : null

  const cartState = useCartProduct(primaryId, cartModifiers)

  const originCartItem = useSelector((state: IRedux) => {
    if (product.type !== 'origin' || product.has_modifier) return null

    return (
      state.cart?.cart?.find((item) => lookupIds.includes(item.product_id)) ??
      null
    )
  })

  if (product.type === 'origin' && !product.has_modifier) {
    return {
      ...cartState,
      quantity: originCartItem?.quantity ?? 1,
      productInCart: originCartItem,
    }
  }

  return cartState
}
